// ============================================
// components/LoginModal.tsx
// 论坛独立登录弹窗（2026-05-23）
//   - 用 App 账号（用户名 + 密码）登录，复用 app-api 的 app-login
//   - 不做注册：没有账号 → 引导下载 App 注册
//   - 登录成功后：存 token → 刷新 community profile（同时验证 app token 在 community-api 可用）
// ============================================

import { useState } from 'react';
import { Puzzle, Eye, EyeOff } from 'lucide-react';
import { callApi, setToken } from '../services/api';
import { refreshCurrentUser } from '../services/auth';

// 官网下载页（没有账号 → 去这里下载 App 注册）
const APP_DOWNLOAD_URL = 'https://hipiido.cloud/#download';

interface Props {
  onClose: () => void;
}

export default function LoginModal({ onClose }: Props) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const canSubmit = username.trim().length > 0 && password.length > 0 && !loading;

  const onSubmit = async () => {
    if (!canSubmit) return;
    setErr('');
    setLoading(true);
    try {
      // 1. 调 app-login（用户名 + 密码）
      const r = await callApi<{ token: string; user: { user_id: string; nickname?: string } }>(
        'app-login',
        { username: username.trim(), password },
        { noAuth: true },
      );
      if (!r.ok || !r.token) {
        setErr(r.message || '用户名或密码错误');
        setLoading(false);
        return;
      }
      // 2. 存 token
      setToken(String(r.token));
      // 3. 刷新 community profile（拿 short_id/头像，同时验证 app token 在 community-api 可用）
      await refreshCurrentUser();
      setLoading(false);
      onClose();
      // 4. 刷新整页：让内容区等所有组件拿到最新登录态（避免"请重新登录"页不刷新）
      window.location.reload();
    } catch (e) {
      setErr('登录失败，请稍后重试');
      setLoading(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') onSubmit();
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center px-4"
      style={{ background: 'rgba(15,23,42,0.45)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-card max-w-md w-full p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-5 text-ink-400 hover:text-ink-600 text-xl leading-none"
          aria-label="关闭"
        >
          ×
        </button>

        <div className="flex justify-center mb-3">
          <Puzzle className="w-11 h-11 text-gold-400" strokeWidth={1.5} />
        </div>
        <h2 className="text-xl font-bold text-ink-900 mb-1.5 text-center">登录 HiPiiDo 创新工场</h2>
        <p className="text-sm text-ink-500 leading-relaxed mb-6 text-center">
          请使用 HiPiiDo App 账号登录
        </p>

        {/* 用户名 */}
        <div className="mb-3">
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="用户名"
            autoFocus
            className="w-full px-4 py-3 rounded-xl border border-ink-300/60 focus:border-gold-400 focus:outline-none text-ink-900 placeholder:text-ink-400 transition-colors"
          />
        </div>

        {/* 密码 */}
        <div className="mb-2 relative">
          <input
            type={showPwd ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="密码"
            className="w-full px-4 py-3 pr-11 rounded-xl border border-ink-300/60 focus:border-gold-400 focus:outline-none text-ink-900 placeholder:text-ink-400 transition-colors"
          />
          <button
            type="button"
            onClick={() => setShowPwd((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-600"
            aria-label={showPwd ? '隐藏密码' : '显示密码'}
          >
            {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>

        {/* 错误提示 */}
        {err && <p className="text-sm text-red-500 mb-2 px-1">{err}</p>}

        {/* 登录按钮 */}
        <button
          onClick={onSubmit}
          disabled={!canSubmit}
          className="btn-primary w-full mt-3 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? '登录中…' : '登录'}
        </button>

        {/* 没有账号引导 */}
        <div className="mt-6 pt-5 border-t border-ink-300/40 text-center">
          <p className="text-sm text-ink-500 mb-3 leading-relaxed">
            还没有账号？请安装 HiPiiDo App 注册账号后再登录
          </p>
          <a
            href={APP_DOWNLOAD_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary w-full inline-block"
          >
            下载 HiPiiDo App
          </a>
        </div>
      </div>
    </div>
  );
}
